import { useEffect, useMemo, useState } from "react";
import { Color, PickingInfo } from "deck.gl";
import { DesignationFeature } from "react-migration/domains/constraints/components/ConstraintLayer/DesignationLayer";
import { RightClick } from "react-migration/components/RightClick";
import { DesignationHatch } from "react-migration/domains/constraints/components/Hatch";
import { CONSTRAINT_LAYER_CLASSES } from "react-migration/domains/constraints/components/ConstraintLayer/ConstraintLayer";
import { getDesignationFillColour } from "react-migration/domains/constraints/designation/style/accessors";
import { getDeckLayerInstanceIds } from "react-migration/lib/map/getLayerInstances";
import { checkKey, useTranslation } from "react-migration/lib/i18n/useTranslation";
import { isDefined } from "react-migration/lib/util/isDefined";
import { getDeckInstance } from "src/js/plugins/deck.gl/deckInstance";
import { SelectionFeature } from "src/js/stores/map/store";
import { LayerTypeRightClickMenuProps } from "../../types";
import { resolveConstraintClick } from "./constraintClickResolver/constraintClickResolver";
import { ConstraintLayerConfig, isSingleConstraintLayer } from "./types";
import { useConstraintsLayerTypeContext } from "./ConstraintsContext";
import { ConstraintsCategory } from "react-migration/layouts/map/Constraints/types";

interface ConstraintMenuItem {
  id: string;
  name: string;
  sub_category_id?: string;
  fillColor: Color;
  selectionFeature: SelectionFeature;
}

export function ConstraintsRightClickMenu({ layer, rightClickLocation, setSelection }: LayerTypeRightClickMenuProps) {
  const { constraintsStore } = useConstraintsLayerTypeContext();
  const layerState = constraintsStore.layers[layer.id];
  const displayByAttribute = layerState.displayBy;
  const { t } = useTranslation();
  const [constraints, setConstraints] = useState<ConstraintMenuItem[]>();

  useEffect(() => {
    function getConstraintPicksFromDeck() {
      const [x, y] = rightClickLocation.deckCoordinate;

      const deck = getDeckInstance();
      const layerIds = getDeckLayerInstanceIds(CONSTRAINT_LAYER_CLASSES);

      return deck?.pickMultipleObjects({ x, y, layerIds });
    }

    async function enrichConstraintPicks(picks: PickingInfo<DesignationFeature>[]) {
      const layerConfig = layer.layerConfig as ConstraintLayerConfig;

      const isLayer = (pick: PickingInfo<DesignationFeature>, layerConfig: ConstraintLayerConfig) => {
        if (!pick.object) return false;

        // hack for any categories that need to be renamed to match sub categories from layerConfig
        const getCategory = (category: string) => {
          if (category === "housing_allocation") return "residential_allocation";
          if (category === "zoning_special") return "zoning_special_outer";
          if (category === "zoning_planned") return "zoning_planned_outer";
          return category;
        };

        const layerRoots = layerConfig?.layerRoots ?? [];
        const subCategories = layerConfig?.categorySchema.map((c) => c.key) ?? [];

        const currentLayerName = pick.object.properties.layerName ?? "";
        const rootCategories = isSingleConstraintLayer(layerConfig)
          ? [layerConfig.rootCategory]
          : layerConfig.rootCategories;
        const isRootLayer = rootCategories.includes(currentLayerName as ConstraintsCategory);
        const isSubCategory = subCategories.includes(getCategory(pick.object.properties.sub_category_id));

        return layerRoots.includes(currentLayerName) || (isRootLayer && isSubCategory);
      };

      const response = (
        await Promise.all(
          picks
            .filter((pick) => isLayer(pick, layerConfig))
            .map(async (pick) => {
              if (!pick.object) return;

              const selectionFeature = await resolveConstraintClick(pick);
              if (!selectionFeature) return;

              const translationKey = `constraints.categories.${pick.object.properties.sub_category_id}`;
              return {
                id: pick.object.properties.designation_id,
                name: checkKey(translationKey) ? t(translationKey) : pick.object.properties.display_name,
                sub_category_id: pick.object.properties.sub_category_id,
                fillColor: getDesignationFillColour(pick.object, displayByAttribute),
                selectionFeature,
              };
            })
        )
      ).filter(isDefined);

      setConstraints(response);
    }

    const constraintPicks = getConstraintPicksFromDeck();
    constraintPicks && enrichConstraintPicks(constraintPicks);
  }, [layer.layerConfig, rightClickLocation.deckCoordinate, t]);

  const bucketedConstraints = useMemo(() => {
    const sortedConstraints = constraints?.sort((a, b) => a.name.localeCompare(b.name));

    if (!sortedConstraints?.length) return [null, null];

    return [sortedConstraints.slice(0, 4), sortedConstraints.slice(4)];
  }, [constraints]);

  if (!bucketedConstraints[0]?.length) return null;

  return (
    <RightClick.Group title={layer.title}>
      {bucketedConstraints[0].map((constraint) => (
        <ConstraintItem key={constraint.id} constraint={constraint} setSelection={setSelection} />
      ))}

      {!!bucketedConstraints[1]?.length && (
        <RightClick.Sub>
          <RightClick.SubTrigger>{bucketedConstraints[1].length} more</RightClick.SubTrigger>
          <RightClick.Portal>
            <RightClick.SubContent>
              {bucketedConstraints[1].map((constraint) => (
                <ConstraintItem key={constraint.id} constraint={constraint} setSelection={setSelection} />
              ))}
            </RightClick.SubContent>
          </RightClick.Portal>
        </RightClick.Sub>
      )}
    </RightClick.Group>
  );
}

interface ConstraintItemProps {
  constraint: ConstraintMenuItem;
  setSelection(selection: SelectionFeature): void;
}

function ConstraintItem({ constraint, setSelection }: ConstraintItemProps) {
  return (
    <RightClick.Item
      onClick={() => setSelection(constraint.selectionFeature)}
      leadComponent={
        constraint.sub_category_id && (
          <DesignationHatch id={constraint.sub_category_id} fillColorOverride={constraint.fillColor} />
        )
      }
    >
      <div className="truncate overflow-hidden" title={constraint.name}>
        {constraint.name}
      </div>
    </RightClick.Item>
  );
}
